<template>
  <NbModal id="modalBulkChangeStatus">
    <template #header>
      <h4 class="heading-4">
        {{ $t("ordersListPage.bulkChangeStatusModal.title") }}
      </h4>
    </template>

    <template #body>
      <p class="mb-3">
        {{
          $t("ordersListPage.bulkChangeStatusModal.selectedOrders", {
            count: orderIds.length,
          })
        }}
      </p>

      <Spinner v-if="loadingCheckpoints" size="lg" class="mx-auto" />

      <NbSelectInput
        v-else
        id="bulk-change-status-select"
        name="bulk-change-status-select"
        :options="checkpointOptions"
        v-model="selectedCheckpoint"
        :placeholder="$t('ordersListPage.bulkChangeStatusModal.selectStatus')"
      />
    </template>

    <template #footer>
      <div class="d-flex justify-content-end">
        <NbButton variant="secondary" data-dismiss="modal">
          {{ $t("cancel") }}
        </NbButton>
        <NbButton
          class="ml-2"
          :disabled="submitting || !selectedCheckpoint.tracking_code"
          @click="submitBulkChangeStatus"
        >
          {{ $t("confirm") }}
        </NbButton>
      </div>
    </template>
  </NbModal>
</template>

<script>
import NbModal from "@/components/modal/NbModal.vue";
import NbButton from "@/components/buttons/NbButton.vue";
import NbSelectInput from "@/components/input/select/NbSelectInput.vue";
import Spinner from "@/components/Spinner.vue";
import CheckpointService from "@/services/CheckpointService";
import OrderService from "@/services/OrderService";

const checkpointService = new CheckpointService();
const orderService = new OrderService();

export default {
  name: "ModalBulkChangeStatus",
  components: {
    NbModal,
    NbButton,
    NbSelectInput,
    Spinner,
  },
  props: {
    orderIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      loadingCheckpoints: false,
      submitting: false,
      checkpointOptions: [],
      selectedCheckpoint: {},
    };
  },
  methods: {
    resetForm() {
      this.selectedCheckpoint = {};
    },
    async loadCheckpoints() {
      try {
        this.loadingCheckpoints = true;
        const { data } = await checkpointService.getCheckpoints();
        const checkpoints = data.data.elements || data.data || [];

        this.checkpointOptions = checkpoints
          .filter((checkpoint) => checkpoint.title && checkpoint.tracking_code)
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((checkpoint) => ({
            id: checkpoint.id,
            text: checkpoint.title,
            value: {
              tracking_code: checkpoint.tracking_code,
              title: checkpoint.title,
            },
          }));
      } catch (error) {
        const errors = this.$helpers.extractErrorMessages(error);
        errors.forEach((message) => {
          this.$helpers.toast(message, "danger");
        });
      } finally {
        this.loadingCheckpoints = false;
      }
    },
    async submitBulkChangeStatus() {
      if (!this.orderIds.length) {
        this.$helpers.toast(
          this.$t("ordersListPage.bulkChangeStatusModal.noOrdersSelected"),
          "warning"
        );
        return;
      }

      if (!this.selectedCheckpoint.tracking_code) {
        this.$helpers.toast(
          this.$t("ordersListPage.bulkChangeStatusModal.selectStatus"),
          "warning"
        );
        return;
      }

      this.submitting = true;

      try {
        const { data } = await orderService.addVolumeCheckpointMulti(
          this.orderIds,
          this.selectedCheckpoint.tracking_code
        );

        const messages = data.messages || [];
        const notExecutedIds = data.data?.not_executed_for_ids || [];

        if (messages.length) {
          this.$helpers.toast(messages[0], "success", 4000);
        }

        if (notExecutedIds.length) {
          this.$helpers.toast(
            this.$t("ordersListPage.bulkChangeStatusModal.partialSuccess", {
              ids: notExecutedIds.join(", "),
            }),
            "warning",
            6000
          );
        }

        this.$helpers.closeModal("modalBulkChangeStatus");
        this.resetForm();
        this.$emit("success");
      } catch (error) {
        const errors = this.$helpers.extractErrorMessages(error);
        errors.forEach((message) => {
          this.$helpers.toast(message, "danger");
        });
      } finally {
        this.submitting = false;
      }
    },
  },
  watch: {
    orderIds() {
      this.resetForm();
    },
  },
  created() {
    this.loadCheckpoints();
  },
};
</script>
